import { useRef, useState, useEffect } from "react";
import { usePreview } from "../store/previewContext";
import * as XLSX from "xlsx";
import {
  IconX,
  IconDownload,
  IconGripVertical,
  IconMaximize,
  IconMinimize,
  IconZoomIn,
  IconZoomOut,
  IconEdit,
  IconCopy,
  IconArrowBackUp,
  IconArrowForwardUp,
  IconFileExport,
} from "@tabler/icons-react";
import { useAppTheme } from "core/contexts/theme/theme";
import CodeMirror from "@uiw/react-codemirror";
import { javascript } from "@codemirror/lang-javascript";
import { python } from "@codemirror/lang-python";
import { json } from "@codemirror/lang-json";
import { html } from "@codemirror/lang-html";
import { css } from "@codemirror/lang-css";
import { markdown } from "@codemirror/lang-markdown";
// Commenting for OSS policy violations - Handsontable (Commercial License), @handsontable/react (Commercial License), hyperformula (GPL-3.0)
// import { HotTable } from "@handsontable/react";
// import "handsontable/dist/handsontable.full.css";
// import Handsontable from "handsontable";
import { ExcelSelectorsWrapper, PreviewPaneWrapper } from "./previewPane.styled";
import { Tooltip } from "@mui/material";


interface PreviewPaneProps {
  workbookData: { [sheet: string]: any[][] };
  setWorkbookData: React.Dispatch<
    React.SetStateAction<{ [sheet: string]: any[][] }>
  >;
  sheetNames: string[];
  setSheetNames: React.Dispatch<React.SetStateAction<string[]>>;
  selectedSheet: string;
  setSelectedSheet: React.Dispatch<React.SetStateAction<string>>;
  excelData: any[][];
  setExcelData: React.Dispatch<React.SetStateAction<any[][]>>;
}

const PreviewPane = ({
  workbookData,
  setWorkbookData,
  sheetNames,
  selectedSheet,
  setSelectedSheet,
  excelData,
  setSheetNames,
  setExcelData,
}: PreviewPaneProps) => {
  const { previewContent, setPreviewContent, previewWidth, setPreviewWidth } =
    usePreview();

  const paneRef: any = useRef(null);
  const [isFullWidth, setIsFullWidth] = useState(false);
  const [prevWidth, setPrevWidth] = useState(50);
  const isResizing = useRef(false);
  const [isEditing, setIsEditing] = useState(false);
  const [undoStack, setUndoStack] = useState<string[]>([]);
  const [codeValue, setCodeValue] = useState("");
  const [redoStack, setRedoStack] = useState<string[]>([]);
  const [imageScale, setImageScale] = useState(1);
  const [mergeCells, setMergeCells] = useState<any[]>([]);
  const [cellMeta, setCellMeta] = useState<Record<string, any>>({});
  const [workbook, setWorkbook] = useState<XLSX.WorkBook | null>(null);
  const { apptheme } = useAppTheme();
  const isDarkTheme = apptheme?.name === "dark-theme";
  // Commenting for OSS policy violations - Handsontable references
  // const hotRef = useRef<any>(null);
  // const hotContainerRef = useRef<HTMLDivElement>(null);
  // const hotInstanceRef = useRef<Handsontable | null>(null);

  // Handle resize drag operations
  const handleMouseDown = (e) => {
    e.preventDefault();
    isResizing.current = true;
    document.body.style.cursor = "ew-resize";
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  const handleMouseMove = (e) => {
    if (!isResizing.current || !paneRef.current) return;

    const parentRect = paneRef.current.parentElement?.getBoundingClientRect();
    if (!parentRect) return;

    const newWidth = parentRect.right - e.clientX;
    const clampedWidth = Math.max(newWidth, 300);
    const percent = Math.min((clampedWidth / parentRect.width) * 100, 100);
    setPreviewWidth(percent);
    setPrevWidth(percent);
  };

  const handleMouseUp = () => {
    isResizing.current = false;
    document.body.style.cursor = "default";
    document.removeEventListener("mousemove", handleMouseMove);
    document.removeEventListener("mouseup", handleMouseUp);
  };

  // Toggle between full width and previous width
  const toggleFullWidth = () => {
    if (isFullWidth) {
      setPreviewWidth(prevWidth);
    } else {
      setPreviewWidth(100);
    }
    setIsFullWidth(!isFullWidth);
  };

  // Zoom controls for images
  const zoomIn = () => setImageScale((prev) => Math.min(prev + 0.25, 3));
  const zoomOut = () => setImageScale((prev) => Math.max(prev - 0.25, 0.5));
  const resetZoom = () => setImageScale(1);

  // Reset zoom when changing files
  useEffect(() => {
    setImageScale(1);
  }, [previewContent?.url]);

  const isExcel = (() => {
    if (!previewContent) return false;
    return (
      previewContent.fileType?.includes("sheet") ||
      /\.(xls|xlsx|csv)$/i.test(previewContent.name || "")
    );
  })();

  useEffect(() => {
    if (!excelData || !excelData.length) return;

    const cleanedData = excelData
      .filter((row) => !row.every((cell) => cell === "merge"))
      .map((row) => {
        // Remove 'merge' from any column in this row
        const cleanedRow = row.map((cell) => (cell === "merge" ? "" : cell));

        // Optional: remove repeated group names like "P2P"
        if (typeof cleanedRow[0] === "string") {
          if (cleanedRow[0]?.toLowerCase() === "p2p") {
            cleanedRow[0] = "";
          }
        }

        return cleanedRow;
    });

    setExcelData(cleanedData);
    setMergeCells([]); // No merge logic
    setCellMeta({});
  }, [excelData.length]);

  function calculateColWidths(
    data: string[][],
    minWidth = 120,
    maxWidth = 400
  ): number[] {
    const colCount = Math.max(...data.map((row) => row.length));
    const colWidths = new Array(colCount).fill(minWidth);

    data.forEach((row) => {
      row.forEach((cell, colIndex) => {
        const contentLength = cell?.toString().length || 0;
        const estimatedWidth = Math.min(
          maxWidth,
          Math.max(minWidth, contentLength * 8)
        ); // ~8px per character
        colWidths[colIndex] = Math.max(colWidths[colIndex], estimatedWidth);
      });
    });

    return colWidths;
  }

  // Commenting for OSS policy violations - Handsontable initialization
  /* useEffect(() => {
    if (hotContainerRef.current) {
      const colWidths = calculateColWidths(excelData);

      hotInstanceRef.current = new Handsontable(hotContainerRef.current, {
        data: excelData,
        colHeaders: true,
        rowHeaders: true,
        colWidths,
        rowHeights: 40,
        licenseKey: "non-commercial-and-evaluation",
        stretchH: "none",
        readOnly: true,
        autoWrapRow: true,
        autoWrapCol: true,
      });
    }

    return () => {
      hotInstanceRef.current?.destroy();
    };
  }, [excelData]); */

  useEffect(() => {
    const fetchExcel = async () => {
      if (isExcel && previewContent?.url) {
        try {
          const res = await fetch(previewContent.url);
          const blob = await res.blob();
          const buffer = await blob.arrayBuffer();
          const wb = XLSX.read(buffer, { type: "array" });
          setWorkbook(wb);
          setSheetNames(wb.SheetNames);
          setSelectedSheet(wb.SheetNames[0]);

          const data = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], {
            header: 1,
            defval: "",
          });
          setExcelData(data as any[][]);
        } catch (err) {
          console.error("Error loading Excel file:", err);
        }
      }
    };
    fetchExcel();
  }, [previewContent?.url, isExcel]);

  const handleSheetChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const sheetName = e.target.value;
    setSelectedSheet(sheetName);
    if (workbook) {
      const sheet = workbook.Sheets[sheetName];
      const json = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "" });
      setExcelData(json as any[][]);
    }
  };

  // Commenting for OSS policy violations - Handsontable data extraction
  const handleSave = () => {
    // Temporarily disabled - replace with AG-Grid implementation
    /* if (hotRef.current) {
      const data = hotRef.current.hotInstance.getData();

      // Update both local sheet and global workbookData
      setExcelData(data);
      setWorkbookData((prev) => ({
        ...prev,
        [selectedSheet]: data,
      }));
    } */
  };

  const handleDownload = () => {
    if (!selectedSheet) return;

    // Commenting for OSS policy violations - using excelData instead of hotRef
    const latestData = excelData || [];
    const updatedWorkbook = {
      ...workbookData,
      [selectedSheet]: latestData,
    };

    const newWb = XLSX.utils.book_new();

    Object.entries(updatedWorkbook).forEach(([sheetName, data]) => {
      const ws = XLSX.utils.aoa_to_sheet(data);
      XLSX.utils.book_append_sheet(newWb, ws, sheetName);
    });

    XLSX.writeFile(newWb, previewContent?.name || "UpdatedWorkbook.xlsx");
  };

  const handleCellStyleChange = (changes: any) => {
    if (changes) {
      const [row, col, oldVal, newVal] = changes[0];
      const data = [...excelData];
      data[row][col] = newVal;
      setExcelData(data);
    }
  };

  useEffect(() => {
    if (!previewContent) return;

    console.log("Preview content:", previewContent);
    if (
      previewContent.name?.includes("txt") ||
      previewContent.name?.match(/\.(js|ts|py|json|html|css|md)$/)
    ) {
      fetch(previewContent.url)
        .then((res) => res.text())
        .then((text) => setCodeValue(text));
    }

    // if (
    //   previewContent.name?.includes("txt")
    // ) {
    //   setCodeValue(previewContent.content || "");
    // }
  }, [previewContent]);


  if (!previewContent) return null;

  // File type detection
  const isImageFile = (
    fileType: string | undefined,
    fileName: string
  ): boolean => {
    const ext = fileName.split(".").pop()?.toLowerCase() || "";

    switch (true) {
      case !!fileType?.startsWith("image/"):
      case ["jpg", "jpeg", "png", "gif", "bmp", "webp", "svg"].includes(ext):
        return true;
      default:
        return false;
    }
  };

  // Usage:
  let isImage = isImageFile(previewContent.fileType, previewContent.name);
  const isPdf =
    previewContent.fileType === "application/pdf" ||
    previewContent.name?.endsWith(".pdf");
  const isCode =
    previewContent.fileType?.includes("text") ||
    previewContent.name?.match(/\.(js|ts|py|json|html|css|md|txt)$/);

  // Tailwind classes based on theme
  const bgColor = isDarkTheme ? "bg-zinc-900" : "bg-white";
  const textColor = isDarkTheme ? "text-zinc-100" : "text-zinc-800";
  const borderColor = isDarkTheme ? "border-zinc-700" : "border-zinc-200";
  const dragHandleColor = isDarkTheme ? "text-zinc-500" : "text-zinc-400";
  const dragHandleHoverColor = isDarkTheme
    ? "hover:text-zinc-300"
    : "hover:text-zinc-600";
  const buttonHoverColor = isDarkTheme
    ? "hover:bg-zinc-800"
    : "hover:bg-zinc-100";
  const headerBgColor = isDarkTheme ? "bg-zinc-800" : "bg-zinc-50";
  const controlsBgColor = isDarkTheme ? "bg-zinc-800/80" : "bg-white/80";

  const handleUndo = () => {
    if (!undoStack.length) return;
    const last = undoStack.pop();
    setRedoStack((prev) => [...prev, codeValue]);
    setCodeValue(last!);
  };

  const handleRedo = () => {
    if (!redoStack.length) return;
    const last = redoStack.pop();
    setUndoStack((prev) => [...prev, codeValue]);
    setCodeValue(last!);
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(codeValue);
    } catch (e) {
      console.error("Copy failed", e);
    }
  };
  const getCodeDownloadDataUri = (filename: string) => {
    const ext = filename.split(".").pop()?.toLowerCase();

    const mimeMap: Record<string, string> = {
      js: "application/javascript",
      py: "text/x-python",
      html: "text/html",
      css: "text/css",
      json: "application/json",
      txt: "text/plain",
    };

    const mimeType = mimeMap[ext || ""] || "text/plain";

    return `data:${mimeType};charset=utf-8,${encodeURIComponent(codeValue)}`;
  };
  const handleCodeChange = (val: string) => {
    setUndoStack((prev) => [...prev, codeValue]);
    setRedoStack([]);
    setCodeValue(val);
  };
  const getLanguageExtension = (filename: string) => {
    if (filename.endsWith(".js") || filename.endsWith(".ts"))
      return javascript();
    if (filename.endsWith(".py")) return python();
    if (filename.endsWith(".json")) return json();
    if (filename.endsWith(".html")) return html();
    if (filename.endsWith(".css")) return css();
    if (filename.endsWith(".md")) return markdown();
    return javascript();
  };

  return (
    <div
      ref={paneRef}
      style={{
        flexGrow: previewWidth,
        flexShrink: 1,
        flexBasis: 0,
        width: 0,
      }}
      className={`h-full ${bgColor} border-l ${borderColor} flex flex-row relative`}
    >
      {/* Resizer handle */}
      <div
        className={`absolute left-0 top-0 bottom-0 w-1 cursor-ew-resize z-20 flex items-center group position-sticky`}
        onMouseDown={handleMouseDown}
        style={{ boxShadow: apptheme.shadows.boxShadow, width: "1rem" }}
      >
        <IconGripVertical
          size={18}
          stroke={1.5}
          color={apptheme.colors.textColor}
        />
      </div>

      <div className="d-flex flex-col w-100">
        {/* Header */}
        <div
          className={`flex items-center justify-between px-4 py-3 ${headerBgColor} border-b ${borderColor}`}
        >
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <span className={`font-medium truncate ${textColor}`}>
              {previewContent.name || "Preview"}
            </span>
          </div>

          <div className="flex items-center gap-2">
            {/* Image zoom controls */}
            {isImage && (
              <>
                <button
                  onClick={zoomOut}
                  className={`p-2 rounded-full ${textColor} ${buttonHoverColor} transition-colors`}
                  title="Zoom out"
                >
                  <IconZoomOut size={18} stroke={1.5} />
                </button>
                <button
                  onClick={resetZoom}
                  className={`p-2 rounded-full ${textColor} ${buttonHoverColor} transition-colors text-xs`}
                  title="Reset zoom"
                >
                  {Math.round(imageScale * 100)}%
                </button>
                <button
                  onClick={zoomIn}
                  className={`p-2 rounded-full ${textColor} ${buttonHoverColor} transition-colors`}
                  title="Zoom in"
                >
                  <IconZoomIn size={18} stroke={1.5} />
                </button>
                <a
                  href={previewContent.url}
                  download={previewContent.name}
                  className={`p-2 rounded-full ${textColor} ${buttonHoverColor} transition-colors`}
                  title="Download"
                >
                  <IconDownload size={18} stroke={1.5} />
                </a>
              </>
            )}

            {isCode && (
              <>
                <button
                  onClick={() => setIsEditing(!isEditing)}
                  className={`p-2 rounded-full ${textColor} ${buttonHoverColor}`}
                  title="Edit"
                >
                  <IconEdit size={18} stroke={1.5} />
                </button>
                <button
                  onClick={handleUndo}
                  className={`p-2 rounded-full ${textColor} ${buttonHoverColor}`}
                  title="Undo"
                >
                  <IconArrowBackUp size={18} stroke={1.5} />
                </button>
                <button
                  onClick={handleRedo}
                  className={`p-2 rounded-full ${textColor} ${buttonHoverColor}`}
                  title="Redo"
                >
                  <IconArrowForwardUp size={18} stroke={1.5} />
                </button>
                <button
                  onClick={handleCopy}
                  className={`p-2 rounded-full ${textColor} ${buttonHoverColor}`}
                  title="Copy"
                >
                  <IconCopy size={18} stroke={1.5} />
                </button>
                <a
                  href={
                    previewContent.fileType?.includes("text") ||
                    previewContent.name?.match(/\.(js|ts|py|json|html|css|md)$/)
                      ? getCodeDownloadDataUri(previewContent.name)
                      : previewContent.url
                  }
                  download={previewContent.name}
                  className={`p-2 rounded-full ${textColor} ${buttonHoverColor}`}
                  title="Download"
                >
                  <IconFileExport size={18} stroke={1.5} />
                </a>
              </>
            )}

            {isExcel && (
              <ExcelSelectorsWrapper className={borderColor}>
                {sheetNames.length > 1 && (
                  <select
                    className="text-sm rounded px-2 py-1"
                    value={selectedSheet}
                    onChange={handleSheetChange}
                  >
                    {sheetNames.map((name) => (
                      <option key={name} value={name}>
                        {name}
                      </option>
                    ))}
                  </select>
                )}
                <button
                  onClick={handleSave}
                  className="text-sm text-white px-3 py-1 rounded"
                >
                  <div className={isDarkTheme ? "text-white" : "text-black"}>
                    <Tooltip title="Save" arrow className="me-2 ">
                      <svg
                        className="w-[19px] h-[19px]"
                        aria-hidden="true"
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke="currentColor"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="1.2"
                          d="M10 3v4a1 1 0 0 1-1 1H5m4 10v-2m3 2v-6m3 6v-3m4-11v16a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V7.914a1 1 0 0 1 .293-.707l3.914-3.914A1 1 0 0 1 9.914 3H18a1 1 0 0 1 1 1Z"
                        />
                      </svg>
                    </Tooltip>
                  </div>
                </button>
                <button
                  onClick={handleDownload}
                  className={`p-2 rounded-full ${textColor} ${buttonHoverColor}`}
                >
                  <Tooltip title="Download" arrow className="me-2 ">
                    <IconFileExport size={18} stroke={1.5} />
                  </Tooltip>
                </button>
              </ExcelSelectorsWrapper>
            )}

            <button
              onClick={toggleFullWidth}
              className={`p-2 rounded-full ${textColor} ${buttonHoverColor}`}
              title="Toggle width"
            >
              {isFullWidth ? (
                <IconMinimize size={18} />
              ) : (
                <IconMaximize size={18} />
              )}
            </button>
            <button
              onClick={() => setPreviewContent(null)}
              className={`p-2 rounded-full ${textColor} ${buttonHoverColor}`}
              title="Close"
            >
              <IconX size={18} />
            </button>
          </div>
        </div>

        {/* Content area */}
        <div className={`flex-1 overflow-auto relative ${textColor}`}>
          {isImage && (
            <div className="flex items-center justify-center h-full p-4">
              <div className="overflow-auto relative w-100 h-100 d-flex justify-content-center">
                <img
                  src={previewContent.url}
                  alt={previewContent.name}
                  className="object-contain rounded-lg"
                  style={{
                    transform: `scale(${imageScale})`,
                    transformOrigin: "center center",
                    transition: "transform 0.2s ease",
                  }}
                />
              </div>
            </div>
          )}

          {isPdf && (
            <div className="h-full w-full">
              <object
                data={previewContent.url}
                type="application/pdf"
                className="w-full h-full"
              >
                <div className="flex flex-col items-center justify-center h-full p-6 text-center">
                  <p className="mb-4">Unable to display PDF directly.</p>
                  <a
                    href={previewContent.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`px-4 py-2 rounded-md ${
                      isDarkTheme
                        ? "bg-blue-600 hover:bg-blue-700"
                        : "bg-blue-500 hover:bg-blue-600"
                    } text-white transition-colors`}
                  >
                    Open PDF in new tab
                  </a>
                </div>
              </object>
            </div>
          )}

          {isExcel && (
            <>
              {excelData.length > 0 && (
                <PreviewPaneWrapper>
                  {/* TODO: Replace with AG-Grid Community component */}
                  <div style={{ padding: '20px', textAlign: 'center' }}>
                    <p>Excel preview temporarily disabled. Package removed due to license policy violation.</p>
                    <p>Download the file to view contents.</p>
                  </div>
                  {/* Commenting for OSS policy violations - HotTable component
                  <HotTable
                    ref={hotRef}
                    data={excelData}
                    colHeaders={true}
                    rowHeaders={true}
                    licenseKey="non-commercial-and-evaluation"
                    className="hot-excel-preview"
                    afterChange={handleCellStyleChange}
                    mergeCells={mergeCells}
                    colWidths={(index) => {
                      const maxContentLength = Math.max(
                        ...excelData.map(
                          (row) => (row[index] || "").toString().length
                        )
                      );
                      return Math.min(Math.max(120, maxContentLength * 8), 400);
                    }}
                    stretchH="none"
                    cells={(row, col) => {
                      const key = `${row}-${col}`;
                      if (cellMeta[key]) {
                        return {
                          renderer: (
                            instance,
                            td,
                            row,
                            col,
                            prop,
                            value,
                            cellProperties
                          ) => {
                            td.innerHTML = cellMeta[key].value;
                            td.className = "htCenter htMiddle htBold";
                          },
                        };
                      }
                      return {};
                    }}
                  /> */}
                </PreviewPaneWrapper>
              )}
            </>
          )}

          {isCode && (
            <div className="h-full overflow-auto">
              <pre
                className={`h-100 ${
                  isDarkTheme ? "bg-zinc-800" : "bg-gray-50"
                } overflow-x-auto`}
              >
                <div className="h-full">
                  <CodeMirror
                    value={codeValue}
                    height="100%"
                    extensions={[getLanguageExtension(previewContent.name)]}
                    theme={isDarkTheme ? "dark" : "light"}
                    editable={isEditing}
                    onChange={handleCodeChange}
                  />
                </div>
              </pre>
            </div>
          )}

          {/* Fallback for unsupported file types */}
          {!isImage && !isPdf && !isCode && !isExcel && (
            <div className="flex flex-col items-center justify-center h-full p-6 text-center">
              <p className="mb-4">
                Preview not available for this file type (
                {previewContent.fileType || "unknown"}).
              </p>
              <a
                href={previewContent.url}
                download={previewContent.name}
                className={`px-4 py-2 rounded-md ${
                  isDarkTheme
                    ? "bg-blue-600 hover:bg-blue-700"
                    : "bg-blue-500 hover:bg-blue-600"
                } text-white transition-colors`}
              >
                Download file
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PreviewPane;
